import React from 'react';

import { cva } from 'class-variance-authority';

import { cn } from './utils';


/**
 * Status pill per the .pen "Status, badges & advisory" (MN2D1) and the
 * DESIGN.md `status-chip` token: pill radius, 11.5px/600 label, soft-tint fill
 * (~13%) + matching stroke (~33%), semantic colors only — never brand-gradient.
 *
 * Two stroke families share the same soft-tint fills:
 *   bordered (default) → the .pen pill: `bg-<token>/13` + `border-<token>/35`.
 *   bordered={false}   → borderless soft-tint (`bg-<token>/13`, no stroke), the
 *                        bespoke feature-table chip used across Garage / Listings
 *                        / Inventory / Sales. Reconciled here (#367) so those
 *                        feature chips adopt the primitive without a visual change.
 *
 * Color variants (fills are identical across both stroke families):
 *   success  → Available / Sold-ok   (green tint)
 *   warning  → Reserved / relist      (amber tint)
 *   info     → Sold / shipped         (indigo tint; semantic, not the gradient)
 *   danger   → destructive / risk     (rose tint)
 *   neutral  → Removed / muted        (ink-subtle tint)
 *   primary  → Available / Active     (brand tint, /15 to match the bespoke fill)
 *   surface  → Removed / fully-parted (surface-2 fill, no token opacity)
 *   outline  → Draft / Archived       (transparent fill, hairline-strong stroke)
 *   suggested→ advisory prefill       (dashed indigo stroke, awaiting confirm)
 *
 * The default padding/text-size mirror the .pen pill; the bespoke feature chips
 * use tighter metrics (e.g. `px-2 py-[3px] text-[11px]`), which call sites pass
 * via `className` (merged last via `cn`, so it always wins).
 *
 * Pass an `icon` (e.g. a check for "Confirmed") to prefix the label.
 */
const statusChipVariants = cva(
  'inline-flex items-center gap-1.5 rounded-full px-[11px] py-1 '
  + 'text-[11.5px] font-semibold leading-none [&_svg]:size-3 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        success: 'bg-success/13 text-success',
        warning: 'bg-warning/13 text-warning',
        info: 'bg-info/13 text-info',
        danger: 'bg-danger/13 text-danger',
        neutral: 'bg-ink-subtle/13 text-ink-subtle',
        primary: 'bg-primary/15 text-primary',
        surface: 'bg-surface-2 text-ink-subtle',
        outline: 'bg-transparent text-ink-subtle',
        suggested: 'bg-primary/10 text-primary',
      },
      bordered: {
        'true': 'border',
        'false': '',
      },
    },
    compoundVariants: [
      { variant: 'success', bordered: true, className: 'border-success/35' },
      { variant: 'warning', bordered: true, className: 'border-warning/35' },
      { variant: 'info', bordered: true, className: 'border-info/35' },
      { variant: 'danger', bordered: true, className: 'border-danger/35' },
      { variant: 'neutral', bordered: true, className: 'border-ink-subtle/35' },
      { variant: 'primary', bordered: true, className: 'border-primary/35' },
      { variant: 'surface', bordered: true, className: 'border-hairline-strong' },
      { variant: 'outline', bordered: true, className: 'border-hairline-strong' },
      { variant: 'suggested', bordered: true, className: 'border-dashed border-primary' },
    ],
    defaultVariants: {
      variant: 'neutral',
      bordered: true,
    },
  },
);

const StatusChip = React.forwardRef((props, ref) => {
  const {
    className,
    variant,
    bordered,
    icon = null,
    children,
    ...restProps
  } = props;

  return (
    <span
      ref={ref}
      className={cn(statusChipVariants({ variant, bordered }), className)}
      {...restProps}
    >
      {icon}
      {children}
    </span>
  );
});

StatusChip.displayName = 'StatusChip';

export { StatusChip, statusChipVariants };
export default StatusChip;
